import { useInstantSearch } from 'react-instantsearch';
import styled from 'styled-components';

import { Currency } from '@sorare/core/src/__generated__/globalTypes';
import { BodyL, LabelS } from '@sorare/core/src/atoms/typography';
import { FilterTitle } from '@sorare/core/src/components/search/FilterTitle';
import { useConfigContext } from '@sorare/core/src/contexts/config';
import { useCurrentUserCurrencies } from '@sorare/core/src/hooks/useCurrentUserCurrencies';
import { CARD_LEVEL_MIN } from '@sorare/core/src/lib/cards';
import type { Filter, FilterWidget } from '@sorare/core/src/lib/filters';
import { FILTERS } from '@sorare/core/src/lib/filters';
import { powerAlgorithm } from '@sorare/core/src/lib/slider';
import { toWei } from '@sorare/core/src/lib/wei';

import { useFormatFilters } from 'hooks/filters/useFormatFilters';
import { RangeSlider } from 'search/RangeSlider';

type TitleProps = {
  filter: Filter;
  short?: boolean;
};

const Title = ({ filter, short }: TitleProps) => {
  const { indexUiState } = useInstantSearch();
  const selected = !!indexUiState.range?.[filter.attribute];

  return (
    <FilterTitle
      name={(short && filter?.shortTitle) || filter.title}
      selected={selected}
    />
  );
};

export const makeRangeFilter = (
  Component: React.FC<{ visible: boolean }>,
  filter: Filter,
  {
    startsOpen = false,
    hideTitle = false,
  }: {
    startsOpen?: boolean;
    hideTitle?: boolean;
  } = {}
): FilterWidget => ({
  key: filter.attribute,
  type: 'range',
  attribute: filter.attribute,
  Component: ({ visible }) => <Component visible={visible} />,
  title: !hideTitle && <Title filter={filter} />,
  shortTitle: !hideTitle && <Title filter={filter} short />,
  accordionOptions: { startsOpen },
});

export const RefineCardLevel = makeRangeFilter(({ visible }) => {
  const {
    so5: { cardMaxGrade },
  } = useConfigContext();
  return (
    <RangeSlider
      attribute={FILTERS.cardLevel.attribute}
      minForced={CARD_LEVEL_MIN}
      maxForced={cardMaxGrade}
      visible={visible}
    />
  );
}, FILTERS.cardLevel);

const refinePriceAlgorithm = powerAlgorithm({ power: 20 });

const REFINE_PRICE_MAX_WEI = toWei(100);

const StyledText20 = styled(BodyL)`
  align-self: center;
`;
const StyledCaption = styled(LabelS)`
  align-self: flex-end;
`;

const PriceRangeSlider = ({ visible }: { visible: boolean }) => {
  const { algoliaWeiFactor, sliderToPrice, priceToSlider } = useFormatFilters();
  const { currency, fiatCurrencySymbol, cryptocurrencySymbol } =
    useCurrentUserCurrencies();

  return (
    <RangeSlider
      attribute={FILTERS.price.attribute}
      algorithm={refinePriceAlgorithm}
      fromInput={priceToSlider}
      toInput={sliderToPrice}
      maxForced={Number(REFINE_PRICE_MAX_WEI / algoliaWeiFactor)}
      greaterThanMode
      icon={
        currency === Currency.FIAT ? (
          <StyledText20 bold color="var(--c-white)">
            {fiatCurrencySymbol}
          </StyledText20>
        ) : (
          <StyledCaption bold color="var(--c-white)">
            {cryptocurrencySymbol}
          </StyledCaption>
        )
      }
      visible={visible}
    />
  );
};

export const RefinePrice = ({
  startsOpen = false,
}: {
  startsOpen?: boolean;
} = {}) =>
  makeRangeFilter(PriceRangeSlider, FILTERS.price, {
    startsOpen,
    hideTitle: true,
  });
